import React, { useState, useEffect, useRef } from 'react';
import { TimeEntry, Activity, Customer, Project } from '../types/TimeEntry';
import { timeRegistrationService } from '../services/TimeRegistrationService';
import { useAuth } from '../contexts/AuthContext';
import './AddEntryModal.css';

interface AddEntryModalProps {
  isOpen: boolean;
  onClose: () => void;
  date: string;
  existingEntries?: TimeEntry[];
  onEntryAdded: (entry: TimeEntry) => void;
  currentUser: { Id: number; Title: string; EMail: string; LoginName: string } | null;
}

interface NewTimeEntry {
  hours: string;
  activityId: string;
  customerId: string;
  projectId: string;
  title: string;
  details: string;
}

const AddEntryModal: React.FC<AddEntryModalProps> = ({
  isOpen,
  onClose,
  date,
  existingEntries = [],
  onEntryAdded,
  currentUser
}) => {
  const [newEntry, setNewEntry] = useState<NewTimeEntry>({
    hours: '',
    activityId: '',
    customerId: '',
    projectId: '',
    title: '',
    details: ''
  });
  const [activities, setActivities] = useState<Activity[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [filteredActivities, setFilteredActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [recentEntries, setRecentEntries] = useState<TimeEntry[]>([]);
  const [displayedRecentCount, setDisplayedRecentCount] = useState(1);
  const [recentEntriesHovered, setRecentEntriesHovered] = useState(false);
  const [hideRecentEntries, setHideRecentEntries] = useState(false);
  const recentEntriesListRef = useRef<HTMLDivElement>(null);
  const { getAccessToken, user } = useAuth();

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [isOpen, onClose]);

  // Handle scroll to load more entries
  useEffect(() => {
    const listElement = recentEntriesListRef.current;
    if (!listElement || !recentEntriesHovered) return;

    const handleScroll = () => {
      // Check if user has scrolled near the bottom
      const { scrollTop, scrollHeight, clientHeight } = listElement;
      if (scrollHeight - scrollTop - clientHeight < 20 && displayedRecentCount < recentEntries.length) {
        setDisplayedRecentCount(prev => Math.min(prev + 3, recentEntries.length));
      }
    };

    listElement.addEventListener('scroll', handleScroll);
    return () => listElement.removeEventListener('scroll', handleScroll);
  }, [displayedRecentCount, recentEntries.length, recentEntriesHovered]);

  // Load more when hovering over recent entries section
  useEffect(() => {
    if (recentEntriesHovered && displayedRecentCount < recentEntries.length) {
      // Load all remaining entries when hovering
      setDisplayedRecentCount(recentEntries.length);
    }
  }, [recentEntriesHovered, displayedRecentCount, recentEntries.length]);

  // Load recent entries only when hovering
  useEffect(() => {
    if (recentEntriesHovered && recentEntries.length === 0 && user) {
      loadRecentEntries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentEntriesHovered, user]);

  // Load activities and customers when modal opens
  useEffect(() => {
    if (isOpen) {
      // Calculate suggested hours: 8 - already registered hours
      const totalRegistered = existingEntries.reduce((sum, entry) => sum + entry.Hours, 0);
      const suggestedHours = Math.max(1, Math.min(24, 8 - totalRegistered));
      
      // Get last used customer from window storage
      const lastCustomerId = (window as any).__lastCustomerId || '';
      
      setNewEntry({
        hours: suggestedHours.toString(),
        activityId: '',
        customerId: lastCustomerId,
        projectId: '',
        title: '',
        details: ''
      });
      setError(null);
      setHideRecentEntries(false);
      loadLookupData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingEntries]);

  const loadLookupData = async () => {
    setLoadingData(true);
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Failed to get access token');
        return;
      }

      // currentUser is fetched once in TimeRegistrationApp - reusing it here (instead of a fresh
      // getCurrentUserInfo call) lets the allowed-projects lookup run in the same parallel batch below.
      const currentUserData = currentUser || await timeRegistrationService.getCurrentUserInfo(accessToken);

      const [activitiesData, customersData, projectsData, allowedProjectIds] = await Promise.all([
        timeRegistrationService.getActivities(accessToken),
        timeRegistrationService.getCustomers(accessToken),
        timeRegistrationService.getProjects(accessToken),
        timeRegistrationService.getAllowedProjectIdsForUser(currentUserData.Id, accessToken)
      ]);

      setActivities(activitiesData.filter(a => a.IsActive !== false));
      const activeProjects = projectsData.filter(p => p.IsActive !== false);

      // External users listed in /Lists/ExternalUsersProjects only see the projects assigned to them there.
      const visibleProjects = allowedProjectIds.length > 0
        ? activeProjects.filter(p => allowedProjectIds.includes(p.Id))
        : activeProjects;
      setProjects(visibleProjects);

      // All users only see customers that have a visible active project.
      const activeCustomers = customersData.filter(c => c.IsActive !== false);
      const customerIdsWithActiveProjects = new Set(
        visibleProjects
          .filter(p => p.Customer?.Id)
          .map(p => p.Customer!.Id)
      );
      const visibleCustomers = activeCustomers.filter(c => customerIdsWithActiveProjects.has(c.Id));
      setCustomers(visibleCustomers);
      setFilteredCustomers(visibleCustomers);

      // If the external user only has a single allowed project, auto-select it (and its customer).
      if (allowedProjectIds.length > 0 && visibleProjects.length === 1) {
        const onlyProject = visibleProjects[0];
        setNewEntry(prev => ({
          ...prev,
          projectId: onlyProject.Id.toString(),
          customerId: onlyProject.Customer ? onlyProject.Customer.Id.toString() : prev.customerId
        }));
        if (onlyProject.Customer) {
          setFilteredCustomers([{ Id: onlyProject.Customer.Id, Title: onlyProject.Customer.Title, IsActive: true }]);
          loadActivitiesForCustomer(onlyProject.Customer.Id, onlyProject.Id);
        }
      }
    } catch (err) {
      console.error('Error loading lookup data:', err);
      setError('Kunde inte ladda aktiviteter, kunder och projekt');
    } finally {
      setLoadingData(false);
    }
  };

  const loadRecentEntries = async () => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) return;

      // Get entries from the current month and previous 2 months to ensure we have enough recent entries
      const now = new Date();
      const entries: TimeEntry[] = [];

      // Load from current month and previous 2 months
      for (let i = 0; i < 3; i++) {
        const checkDate = new Date(now);
        checkDate.setMonth(checkDate.getMonth() - i);
        const year = checkDate.getFullYear();
        const month = checkDate.getMonth();
        
        try {
          const monthEntries = await timeRegistrationService.getTimeEntries(year, month, accessToken);
          entries.push(...monthEntries);
        } catch (err) {
          console.error(`Error loading entries for ${year}-${month}:`, err);
        }
      }
      
      // Sort by date descending and get the 10 most recent
      const recent = entries
        .sort((a, b) => new Date(b.Date).getTime() - new Date(a.Date).getTime())
        .slice(0, 10);
      
      setRecentEntries(recent);
    } catch (err) {
      console.error('Error loading recent entries:', err);
      // Don't show error, this is just a nice-to-have feature
    }
  };

  const formatDate = (dateStr: string) => {
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('sv-SE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleInputChange = (field: keyof NewTimeEntry, value: string) => {
    setNewEntry(prev => ({ ...prev, [field]: value }));

    // If customer is changed, filter activities for that customer
    if (field === 'customerId' && value) {
      const customerId = parseInt(value);
      loadActivitiesForCustomer(customerId);
      // Reset project if customer changed
      setNewEntry(prev => ({ ...prev, projectId: '', activityId: '' }));
    } else if (field === 'customerId' && !value) {
      setFilteredActivities([]);
      setNewEntry(prev => ({ ...prev, projectId: '', activityId: '' }));
    }

    // If project is changed, filter customers
    if (field === 'projectId' && value) {
      const selectedProject = projects.find(p => p.Id.toString() === value);
      if (selectedProject && selectedProject.Customer) {
        // Show only the customer associated with this project
        setFilteredCustomers([{
          Id: selectedProject.Customer.Id,
          Title: selectedProject.Customer.Title,
          IsActive: true
        }]);
        // Auto-select the project's customer and load its activities
        const customerId = selectedProject.Customer.Id;
        setNewEntry(prev => ({
          ...prev,
          projectId: value,
          customerId: customerId.toString(),
          activityId: ''
        }));
        loadActivitiesForCustomer(customerId, parseInt(value));
      } else {
        // If no customer is associated, show all customers
        setFilteredCustomers(customers);
        setNewEntry(prev => ({ ...prev, projectId: value, customerId: '', activityId: '' }));
        setFilteredActivities([]);
      }
    } else if (field === 'projectId' && !value) {
      // If no project selected, show all customers
      setFilteredCustomers(customers);
      if (newEntry.customerId) {
        loadActivitiesForCustomer(parseInt(newEntry.customerId));
      } else {
        setFilteredActivities([]);
      }
      setNewEntry(prev => ({ ...prev, activityId: '' }));
    }
  };

  const loadActivitiesForCustomer = async (customerId: number, projectId?: number) => {
    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        return;
      }
      const activitiesForCustomer = await timeRegistrationService.getActivitiesForCustomer(customerId, accessToken, projectId);
      setFilteredActivities(activitiesForCustomer.filter(a => a.IsActive !== false));
    } catch (err) {
      console.error('Error loading activities for customer:', err);
      setFilteredActivities([]);
    }
  };

  const handleCopyEntry = (entry: TimeEntry) => {
    // Fill the form with data from the selected recent entry
    setNewEntry({
      hours: entry.Hours.toString(),
      activityId: entry.Activity.Id.toString(),
      customerId: entry.Customer.Id.toString(),
      projectId: entry.Project?.Id.toString() || '',
      title: entry.Title || '',
      details: entry.Details || ''
    });
    
    // Load activities for the selected customer
    loadActivitiesForCustomer(entry.Customer.Id, entry.Project?.Id);
    
    // Hide recent entries section
    setHideRecentEntries(true);
  };

  const incrementHours = (step: number) => {
    const currentHours = parseFloat(newEntry.hours) || 0;
    const newHours = Math.max(1, Math.min(24, currentHours + step));
    setNewEntry(prev => ({ ...prev, hours: newHours.toString() }));
  };

  const handleAddEntry = async () => {
    if (!newEntry.hours || !newEntry.activityId || !newEntry.customerId) {
      setError('Timmar, aktivitet och kund är obligatoriska');
      return;
    }

    const hours = parseFloat(newEntry.hours);
    if (isNaN(hours) || hours <= 0 || hours > 24) {
      setError('Vänligen ange giltiga timmar (0,1 - 24)');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        setError('Kunde inte få åtkomsttoken');
        return;
      }

      // Find the selected activity and customer
      const selectedActivity = activities.find(a => a.Id.toString() === newEntry.activityId);
      const selectedCustomer = customers.find(c => c.Id.toString() === newEntry.customerId);
      const selectedProject = newEntry.projectId ? projects.find(p => p.Id.toString() === newEntry.projectId) : null;

      if (!selectedActivity || !selectedCustomer) {
        setError('Vald aktivitet eller kund hittades inte');
        return;
      }

      const entryToAdd: Omit<TimeEntry, 'Id'> = {
        Title: newEntry.title || '',
        Date: date,
        Hours: hours,
        Activity: { Id: selectedActivity.Id, Title: selectedActivity.Title },
        Customer: { Id: selectedCustomer.Id, Title: selectedCustomer.Title },
        Person: currentUser || {
          Id: 0,
          Title: user?.name || user?.username || 'Nuvarande användare',
          EMail: user?.username || '',
          LoginName: user?.username || ''
        },
        Details: newEntry.details || undefined,
        ...(selectedProject && { Project: { Id: selectedProject.Id, Title: selectedProject.Title } })
      };

      const addedEntry = await timeRegistrationService.addTimeEntry(entryToAdd, accessToken);
      
      // Store the last used customer ID for next time
      (window as any).__lastCustomerId = newEntry.customerId;
      
      onEntryAdded(addedEntry);
      onClose();

    } catch (err) {
      console.error('Error adding entry:', err);
      setError(err instanceof Error ? err.message : 'Kunde inte lägga till tidsregistrering');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content add-entry-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Lägg till registrering för {formatDate(date)}</h2>
          <button className="close-button" onClick={onClose} aria-label="Stäng">×</button>
        </div>

        <div className="modal-body">
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-row">
            <div className="form-group form-group-hours">
              <label htmlFor="hours">Timmar *</label>
              <div className="hours-control">
                <button
                  type="button"
                  className="hours-btn"
                  onClick={() => incrementHours(-1)}
                  disabled={loading || loadingData}
                >
                  −
                </button>
                <input
                  id="hours"
                  type="number"
                  step="1"
                  min="1"
                  max="24"
                  value={newEntry.hours}
                  onChange={(e) => handleInputChange('hours', e.target.value)}
                  placeholder="8"
                  disabled={loading || loadingData}
                  autoFocus
                  className="hours-input"
                />
                <button
                  type="button"
                  className="hours-btn"
                  onClick={() => incrementHours(1)}
                  disabled={loading || loadingData}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="customer">Kund *</label>
              {loadingData ? (
                <div className="select-skeleton" aria-hidden="true" />
              ) : (
                <select
                  id="customer"
                  value={newEntry.customerId}
                  onChange={(e) => handleInputChange('customerId', e.target.value)}
                  disabled={loading}
                >
                  <option value="">Välj kund...</option>
                  {filteredCustomers.map(customer => (
                    <option key={customer.Id} value={customer.Id.toString()}>
                      {customer.Title}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          {!loadingData && newEntry.customerId && projects.some(p => p.Customer?.Id.toString() === newEntry.customerId) && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="project">Projekt</label>
                <select
                  id="project"
                  value={newEntry.projectId}
                  onChange={(e) => handleInputChange('projectId', e.target.value)}
                  disabled={loading || loadingData}
                >
                  <option value="">Välj projekt...</option>
                  {projects
                    .filter(p => p.Customer?.Id.toString() === newEntry.customerId)
                    .map(project => (
                      <option key={project.Id} value={project.Id.toString()}>
                        {project.Title}
                      </option>
                    ))}
                </select>
              </div>
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="activity">Aktivitet *</label>
              {loadingData ? (
                <div className="select-skeleton" aria-hidden="true" />
              ) : (
                <select
                  id="activity"
                  value={newEntry.activityId}
                  onChange={(e) => handleInputChange('activityId', e.target.value)}
                  disabled={loading || !newEntry.customerId}
                >
                  <option value="">{newEntry.customerId ? 'Välj aktivitet...' : 'Välj först en kund...'}</option>
                  {filteredActivities.map(activity => (
                    <option key={activity.Id} value={activity.Id.toString()}>
                      {activity.Title}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="title">Beskrivning</label>
              <input
                id="title"
                type="text"
                value={newEntry.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Kort beskrivning av arbetet"
                disabled={loading || loadingData}
              />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="details">Längre kommentar/kom ihåg</label>
              <textarea
                id="details"
                value={newEntry.details}
                onChange={(e) => handleInputChange('details', e.target.value)}
                placeholder="Detaljerad beskrivning eller saker att komma ihåg"
                disabled={loading || loadingData}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Recent Entries Section */}
        {!hideRecentEntries && (
          <div 
            className="recent-entries-section"
            onMouseEnter={() => setRecentEntriesHovered(true)}
            onMouseLeave={() => setRecentEntriesHovered(false)}
          >
          <h3>Senaste registreringar {recentEntries.length === 0 && !recentEntriesHovered ? <span className="recent-entries-hint">ℹ️ hovra för att visa</span> : ''}</h3>
          {recentEntries.length > 0 && (
            <div className="recent-entries-list" ref={recentEntriesListRef}>
              {recentEntries.slice(0, displayedRecentCount).map((entry) => (
                <div key={entry.Id} className="recent-entry-item">
                  <div className="recent-entry-info">
                    <div className="recent-entry-date">{formatDate(entry.Date)}</div>
                    <div className="recent-entry-details">
                      <span className="recent-entry-customer">{entry.Customer.Title}</span>
                      {' - '}
                      <span className="recent-entry-activity">{entry.Activity.Title}</span>
                      {' ('}
                      <span className="recent-entry-hours">{entry.Hours}h</span>
                      {')'}
                    </div>
                    {entry.Title && <div className="recent-entry-title">{entry.Title}</div>}
                  </div>
                  <button
                    type="button"
                    className="copy-entry-btn"
                    onClick={() => handleCopyEntry(entry)}
                    title="Kopiera denna registrering"
                    disabled={loading || loadingData}
                  >
                    Kopiera
                  </button>
                </div>
              ))}
            </div>
          )}
          {recentEntries.length === 0 && recentEntriesHovered && (
            <div className="recent-entries-loading">Laddar senaste registreringar...</div>
          )}
        </div>
        )}

        <div className="modal-footer">
          <button
            onClick={handleAddEntry}
            className="add-button"
            disabled={loading || loadingData || !newEntry.hours || !newEntry.activityId || !newEntry.customerId}
          >
            {loading ? 'Lägger till...' : 'Lägg till'}
          </button>
          <button onClick={onClose} className="cancel-button" disabled={loading}>
            Avbryt
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEntryModal;